import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Survey results — the copy and the arithmetic, kept out of the screens (P14-03; AMENDED 2026-10-03).
 *
 * Everything here is a pure function of a view the gateway already returned. Nothing reads a clock,
 * nothing reaches for a gateway and nothing divides: the shares arrive as basis points from the API's
 * SQL, so a figure on screen can only be wrong if the seam was wrong.
 *
 * AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03). The results are read from the survey
 * interviewer's own answers (`survey_answer`), not from survey rounds, so the round wording (the delta
 * against an earlier round, the question-set boundary, scale averages, open-answer themes) is gone with
 * the rounds. What replaced it:
 *
 *   **The answer vocabulary is worded here, in English, as the rest of the console is.** `survey_answer`
 *   keeps `strongly_agree`, `yes`, `maybe`, `no`, … and the emotion and source ids; the questionnaire's
 *   own TEXT (the statements) is shown in the conversation's language, never translated here.
 *   **A statement's answers read in scale order** — strongly agree to strongly disagree, then the
 *   non-answers — so two statements' bars line up; an emotion's or a source's read most named first.
 *   **Real conversations unless asked.** The note under the figures always says which: how many test
 *   conversations were left out, or that the ones shown are marked Test.
 */
import { percentText } from "../../api/outbound-view.js";
import { languageNameOf } from "../../api/conversation-view.js";
import type {
  SurveyAnswerOptionView,
  SurveyDownloadFormatView,
  SurveyGivenAnswerView,
  SurveyQuestionKindView,
  SurveyQuestionResultView,
  SurveyHangUpKindView,
  SurveyRespondentOutcomeView,
  SurveyResultRangeView,
  SurveyResultView,
} from "../../api/gateway.js";

/** `1,206`. Grouped, because a five-digit count with no separator is misread. */
export function countText(count: number): string {
  return count.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-IE"));
}

/** What a figure shows when there is nothing to measure yet. Never "0" or "0%". */
export const NOT_MEASURED_TEXT = "—";

/** What a cell shows when this person did not answer this question. */
export const NO_ANSWER_TEXT = "—";

// --- outcomes -----------------------------------------------------------------------------------

export type RespondentFilterValue = SurveyRespondentOutcomeView | "all";

/** The outcome chips: every value the route accepts, so a link's filter is always reachable by hand. */
export const RESPONDENT_FILTER: readonly { readonly value: RespondentFilterValue; readonly label: string }[] = [
  { value: "all", get label() { return portalText("operations", "all_6a720856"); } },
  { value: "completed", get label() { return portalText("operations", "completed_1798b3ba"); } },
  { value: "partial", get label() { return portalText("operations", "partial_65de2e2a"); } },
  { value: "declined", get label() { return portalText("operations", "declined_ff59b80f"); } },
  { value: "no_answer", get label() { return portalText("operations", "no_answer_a9c16dd0"); } },
  { value: "no_outcome", get label() { return portalText("operations", "no_outcome_yet_41d7bdbb"); } },
];

const OUTCOME_COPY: Readonly<Record<SurveyRespondentOutcomeView, string>> = {
  get completed() { return portalText("operations", "completed_1798b3ba"); },
  get partial() { return portalText("operations", "partial_65de2e2a"); },
  get declined() { return portalText("operations", "declined_ff59b80f"); },
  get no_answer() { return portalText("operations", "no_answer_a9c16dd0"); },
  get no_outcome() { return portalText("operations", "no_outcome_yet_41d7bdbb"); },
};

export function respondentStatusLabel(outcome: SurveyRespondentOutcomeView): string {
  return OUTCOME_COPY[outcome];
}

/** plan-033 WAPP-HUP: the two kinds of a `partial` respondent. */
const HANG_UP_COPY: Readonly<Record<SurveyHangUpKindView, string>> = {
  get hung_up_before_first_question() { return portalText("shell", "survey_hang_up.hung_up_before_first_question"); },
  get hung_up_mid_way() { return portalText("shell", "survey_hang_up.hung_up_mid_way"); },
};

/** A respondent's status as the table shows it: a `partial` one by its kind of hang-up, when the API names it. */
export function respondentRowStatusLabel(one: { readonly outcome: SurveyRespondentOutcomeView; readonly hangUpKind: SurveyHangUpKindView | null }): string {
  return one.hangUpKind === null ? respondentStatusLabel(one.outcome) : HANG_UP_COPY[one.hangUpKind];
}

/**
 * The tag role for an outcome. Completed is the only positive one; declining is the system working,
 * not a fault, so it is muted.
 */
export function respondentStatusTone(outcome: SurveyRespondentOutcomeView): "positive" | "caution" | "muted" {
  if (outcome === "completed") return "positive";
  if (outcome === "partial") return "caution";
  return "muted";
}

// --- the filter ---------------------------------------------------------------------------------

export const RANGE_OPTION: readonly { readonly value: SurveyResultRangeView; readonly label: string }[] = [
  { value: "all", get label() { return portalText("operations", "all_time_dbad49d8"); } },
  { value: "90d", get label() { return portalText("operations", "last_90_days_c328508b"); } },
  { value: "30d", get label() { return portalText("operations", "last_30_days_6b329852"); } },
  { value: "14d", get label() { return portalText("operations", "last_14_days_73fccbac"); } },
  { value: "7d", get label() { return portalText("operations", "last_7_days_df833fe8"); } },
  { value: "24h", get label() { return portalText("operations", "last_24_hours_99d63362"); } },
];

export function isSurveyResultRange(value: string): value is SurveyResultRangeView {
  return RANGE_OPTION.some((one) => one.value === value);
}

/** "Greek · 412", for the language selector. */
export function languageOptionText(languageCode: string, conversationCount: number): string {
  return `${languageNameOf(languageCode)} · ${countText(conversationCount)}`;
}

// --- the answer vocabulary ----------------------------------------------------------------------

/** A statement's answers, in scale order. The position is the bar order on screen. */
const STATEMENT_ANSWER = () => ([
  ["strongly_agree", portalText("operations", "strongly_agree_64c0edfb")],
  ["yes", portalText("operations", "agree_ee68d349")],
  ["maybe", portalText("operations", "maybe_56dd8d0b")],
  ["no", portalText("operations", "disagree_024ee32c")],
  ["strongly_disagree", portalText("operations", "strongly_disagree_3ac48e5d")],
  ["dont_know", portalText("operations", "don_t_know_3bb9b8e8")],
  ["no_answer", portalText("operations", "no_answer_a9c16dd0")],
  ["unclear", portalText("operations", "unclear_ff75e0dc")],
]);

const EMOTION_ANSWER: Readonly<Record<string, string>> = {
  get anger() { return portalText("operations", "anger_bb2b2e33"); },
  get fear() { return portalText("operations", "fear_689e027c"); },
  get disappointment() { return portalText("operations", "disappointment_f3f36579"); },
  get indifference() { return portalText("operations", "indifference_5bc7ebfe"); },
  get hope() { return portalText("operations", "hope_183e3fcd"); },
  get satisfaction() { return portalText("operations", "satisfaction_6b04c5a7"); },
};

const SOURCE_ANSWER: Readonly<Record<string, string>> = {
  get radio() { return portalText("operations", "radio_b11bf19e"); },
  tv: "TV",
  get internet() { return portalText("operations", "internet_66c06c11"); },
  get social() { return portalText("operations", "social_media_7e89cf43"); },
  get other() { return portalText("operations", "somewhere_else_3a146a72"); },
  get none() { return portalText("operations", "nowhere_5a5b6415"); },
};

/** The three non-answers any question may be given, worded as a statement's are. */
function nonAnswerLabel(answer: string): string | null {
  if (answer !== "dont_know" && answer !== "no_answer" && answer !== "unclear") return null;
  return STATEMENT_ANSWER().find(([value]) => value === answer)?.[1] ?? null;
}

/**
 * How a recorded value reads. A value this console does not know reads as itself. ADR 0053: a choice
 * question's option reads by its questionnaire label (the caller's `label`), a number as itself, an open
 * question's `answered` as "In their own words".
 */
export function answerLabel(kind: SurveyQuestionKindView, answer: string): string {
  if (kind === "statement") return STATEMENT_ANSWER().find(([value]) => value === answer)?.[1] ?? answer;
  if (kind === "emotion") return EMOTION_ANSWER[answer] ?? answer;
  if (kind === "source") return SOURCE_ANSWER[answer] ?? answer;
  if (kind === "open" && answer === "answered") return portalText("operations", "in_their_own_words_ad53a001");
  return nonAnswerLabel(answer) ?? answer;
}

/** A question's options in reading order: scale order for a statement, most named first otherwise. */
export function orderedOption(question: SurveyQuestionResultView): readonly SurveyAnswerOptionView[] {
  if (question.questionKind === "statement") {
    const rank = (answer: string): number => {
      const index = STATEMENT_ANSWER().findIndex(([value]) => value === answer);
      return index === -1 ? STATEMENT_ANSWER().length : index;
    };
    return [...question.option].sort((left, right) => rank(left.answer) - rank(right.answer));
  }
  if (question.questionKind === "number") {
    // A number question's values in numeric order, the non-answers after them.
    const numeric = (answer: string): number => (/^-?\d+(\.\d+)?$/u.test(answer) ? Number(answer) : Number.POSITIVE_INFINITY);
    return [...question.option].sort((left, right) => numeric(left.answer) - numeric(right.answer) || left.answer.localeCompare(right.answer));
  }
  return [...question.option].sort(
    (left, right) => right.count - left.count || right.mentionCount - left.mentionCount || left.answer.localeCompare(right.answer),
  );
}

/** ADR 0053: "Average 5.5 · lowest 4 · highest 7 · 2 numbers" for a number question; `null` before any number. */
export function numberSummaryText(question: Pick<SurveyQuestionResultView, "number">): string | null {
  const number = question.number;
  if (number === undefined) return null;
  return portalText("operations", "average_value_lowest_value_highest_value_ad53a002", {
    v0: String(Number(number.average)), v1: number.minimum, v2: number.maximum, v3: countText(number.count),
  });
}

/** One respondent's answer in a table cell: the label, or every option named. */
export function answerCellText(given: SurveyGivenAnswerView): string {
  // ADR 0053: an open answer's own words, when this person may read them.
  if (given.openText !== undefined) return given.openText;
  const label = (value: string): string => given.optionLabel?.[value] ?? answerLabel(given.questionKind, value);
  if (given.mentioned.length > 1) return given.mentioned.map(label).join(", ");
  return label(given.answer);
}

/** "2 not sure" beside an option the interviewer was unsure of; `null` when it was sure of all. */
export function unsureText(option: SurveyAnswerOptionView): string | null {
  return option.unsureCount === 0 ? null : portalText("operations", "value_not_sure_c2964300", { v0: String(countText(option.unsureCount)) });
}

/** "Named by 14" for an emotion or a source option named more often than it was the answer. */
export function mentionText(kind: SurveyQuestionKindView, option: SurveyAnswerOptionView): string | null {
  if ((kind !== "emotion" && kind !== "source") || option.mentionCount <= option.count) return null;
  return portalText("operations", "named_by_value_fd8879b4", { v0: String(countText(option.mentionCount)) });
}

/**
 * A share, from the API. With no answers to the question there is no share at all, which is not
 * "0% said this".
 */
export function shareText(shareBasisPoint: number, answeredCount: number): string {
  return answeredCount === 0 ? NOT_MEASURED_TEXT : percentText(shareBasisPoint);
}

export function answeredText(answeredCount: number): string {
  return portalText("operations", "value_answered_04d457a1", { v0: String(countText(answeredCount)) });
}

// --- the questions ------------------------------------------------------------------------------

/**
 * The language a results page words its questions in: the language chosen, else the one most
 * respondents spoke, else whatever the questionnaire has.
 */
export function resultLanguageOf(result: Pick<SurveyResultView, "language">, chosen: string | null): string | null {
  return chosen ?? result.language[0]?.languageCode ?? null;
}

/** The question as its questionnaire words it in `languageCode`, or in its first language; `null` with none. */
export function questionTextOf(text: Readonly<Record<string, string>>, languageCode: string | null): string | null {
  if (languageCode !== null && text[languageCode] !== undefined) return text[languageCode];
  return Object.values(text)[0] ?? null;
}

/** "Arm A · statement 2", "Emotion question", "Source question", ADR 0053's "Arm A · question 3". */
export function questionPlaceText(question: Pick<SurveyQuestionResultView, "questionKind" | "variant" | "ordinal">): string {
  if (question.questionKind === "emotion") return portalText("operations", "emotion_question_9fa8c5a8");
  if (question.questionKind === "source") return portalText("operations", "source_question_9dd2c5f3");
  if (question.questionKind !== "statement") {
    return question.variant === null
      ? portalText("operations", "question_value_ad53a003", { v0: String(question.ordinal) })
      : portalText("operations", "arm_value_question_value_ad53a004", { v0: String(question.variant), v1: String(question.ordinal) });
  }
  return question.variant === null ? portalText("operations", "statement_value_cf7ddd9f", { v0: String(String(question.ordinal)) }) : portalText("operations", "arm_value_statement_value_a0a6f257", { v0: String(question.variant), v1: String(String(question.ordinal)) });
}

export const NO_QUESTION_TEXT = () => (portalText("operations", "no_questionnaire_wording_for_this_question_it_is_shown_by_cc46f45c"));

// --- the figures --------------------------------------------------------------------------------

export interface FigureCopy {
  readonly value: string;
  readonly label: string;
}

export function completionFigure(completionBasisPoint: number | null): FigureCopy {
  return completionBasisPoint === null
    ? { value: NOT_MEASURED_TEXT, label: portalText("operations", "completion_rate_no_conversations_yet_74bf2da5") }
    : { value: percentText(completionBasisPoint), label: portalText("operations", "completion_rate_a3600837") };
}

/** The headline figures, in the order the page draws them. */
export function resultFigures(result: SurveyResultView): readonly FigureCopy[] {
  return [
    { value: countText(result.conversationCount), label: portalText("operations", "conversations_07c59b44") },
    { value: countText(result.completedCount), label: portalText("operations", "completed_1798b3ba") },
    completionFigure(result.completionBasisPoint),
    // plan-033 WAPP-HUP (design §44.10): Partial as its two kinds, always (decoder tightened 2026-10-29, §44.12).
    { value: countText(result.hungUpMidWayCount), label: HANG_UP_COPY.hung_up_mid_way },
    { value: countText(result.hungUpBeforeFirstQuestionCount), label: HANG_UP_COPY.hung_up_before_first_question },
    { value: countText(result.declinedCount), label: portalText("operations", "declined_ff59b80f") },
    { value: countText(result.noAnswerCount), label: portalText("operations", "no_answer_a9c16dd0") },
  ];
}

/** The note under the figures: which conversations these are, said every time. */
export function scopeNoteText(result: Pick<SurveyResultView, "isTestCount" | "hiddenIsTestCount" | "noOutcomeCount">, includeIsTest: boolean): string {
  const isTestText = includeIsTest
    ? result.isTestCount === 0
      ? portalText("operations", "test_conversations_are_included_there_are_none_in_this_vie_7835fc3f")
      : portalText("operations", "survey.testIncluded", { count: result.isTestCount, v0: countText(result.isTestCount) })
    : result.hiddenIsTestCount === 0
      ? portalText("operations", "real_conversations_only_2caa550c")
      : portalText("operations", "survey.testExcluded", { count: result.hiddenIsTestCount, v0: countText(result.hiddenIsTestCount) });
  const running =
    result.noOutcomeCount === 0
      ? ""
      : portalText("operations", "survey.noOutcome", { count: result.noOutcomeCount, v0: countText(result.noOutcomeCount) });
  return `${isTestText}${running}`;
}

// --- the download -------------------------------------------------------------------------------

interface DownloadFormatOption {
  readonly value: SurveyDownloadFormatView;
  readonly label: string;
  readonly dek: string;
}

/**
 * The four formats the design draws, every one written by the API (AMENDED 2026-11-03, design §16.7.2 AS AMENDED
 * 2026-11-03): the same rows and the same filter in each.
 */
export const DOWNLOAD_FORMAT: readonly DownloadFormatOption[] = [
  {
    value: "csv",
    label: "CSV",
    get dek() { return portalText("operations", "one_row_per_conversation_one_column_per_question_opens_in_645e354b"); },
  },
  { value: "xlsx", get label() { return portalText("operations", "excel_a08e1111"); }, get dek() { return portalText("operations", "survey_download.xlsx_dek"); } },
  { value: "json", label: "JSON", get dek() { return portalText("operations", "survey_download.json_dek"); } },
  { value: "pdf", get label() { return portalText("operations", "pdf_report_de28b07a"); }, get dek() { return portalText("operations", "survey_download.pdf_dek"); } },
];

/** Who the file is a census of, before it is generated. The exact count arrives with the file. */
export function downloadCensusText(includeDeclinedAndNoAnswer: boolean, conversationCount: number): string {
  return includeDeclinedAndNoAnswer
    ? portalText("operations", "every_conversation_on_this_page_value_rows_5dc5f1a6", { v0: String(countText(conversationCount)) })
    : portalText("operations", "every_conversation_on_this_page_with_at_least_one_answer_c_438c0c77");
}

export function downloadReadyText(rowCount: number, expiresAtText: string): string {
  return (
    portalText("operations", "the_file_is_ready_value_value_the_link_expires_value_ask_a_7a65a0a9", { v0: String(countText(rowCount)), v1: String(rowCount === 1 ? portalText("operations", "row_e8cdc05b") : portalText("operations", "rows_6c30d261")), v2: String(expiresAtText) })
  );
}

export function downloadExtensionOf(format: SurveyDownloadFormatView): string {
  return `.${format}`;
}

/**
 * "Figures as they stood at 14:02." From the query's `dataUpdatedAt`, never from a clock read during
 * render.
 */
export function asAtText(dataUpdatedAt: number): string {
  if (dataUpdatedAt <= 0) return portalText("operations", "figures_have_not_been_loaded_yet_27915be7");
  const at = new Date(dataUpdatedAt);
  const clock = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
  return portalText("operations", "figures_as_they_stood_at_value_a_download_is_generated_fro_d9032b21", { v0: String(clock) });
}
